import Loader from '@cp/src/components/Loader/Loader';
import { RootState } from '@cp/src/store';
import { readQueryParams } from '@cp/src/utils/QueryParamsHelper';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import SingleTicketDrawer from '../../CsaTicketing/components/SingleTicketDrawer';
import RenderAgTable from '../../ExternalDashboardSensei/components/RenderAgTable';
import { getFieldSupportColumnDefs } from './columnDefs';
import styles from '../index.module.scss';
import {
  acknowledgeTicket,
  fetchSingleTicket
} from '../../CsaTicketing/actions/CsaTicketingActions';
import { fetchDynamicFilters, fetchTicketsForTable } from '../actions/CsaCoordintationActions';
import TableHeader from '../TableHeader';
import { CSA_POLLING_TIME, FIELD_SUPPORT_TABLE } from '../contants';
import { ITableTicket, TableName } from '../types';
import { getUpdatedColDefs, setInitialParams, updateAcknowlegementStatus } from '../utils';
import { poll } from '@cp/src/utils/polling';
import { noop } from '@navi/web-ui/lib/utils/common';
import { CLICKSTREAM_EVENT_NAMES } from '@cp/src/service/clickStream.constant';
import { addClickstreamEvent } from '@cp/src/service/clickStreamEventService';

const FieldSupport = () => {
  const queryParams = readQueryParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const unsubscribe = useRef<() => void>();

  const [showSingleTicketDrawer, setShowSingleTicketDrawer] = useState(false);

  const fieldSupportTickets = useSelector(
    (state: RootState) => state?.csaCoordination?.requestedFieldSupport
  );
  const tableDataIsLoading = useSelector((state: RootState) => state?.csaCoordination?.isLoading);
  const fieldSupportFilterSchema = useSelector(
    (state: RootState) => state?.csaCoordination?.fieldSupportFilterSchema
  );
  const singleTicket = useSelector(
    (state: RootState) => state?.csaTicketing?.ticket?.ticketDetails
  );
  const singleTicketLoading = useSelector(
    (state: RootState) => state?.csaTicketing?.ticket?.isLoading
  );
  const userId = useSelector((state: RootState) => state?.common?.userData?.referenceId);

  const { data = [], pages } = fieldSupportTickets || {};

  const totalElements = pages?.totalElements || 0;
  const currentPage = Number(queryParams?.fieldSupport?.pageNumber) || 1;
  const pageSize = Number(queryParams?.fieldSupport?.pageSize) || 10;

  useEffect(() => {
    addClickstreamEvent(CLICKSTREAM_EVENT_NAMES.LH_FE_TASKS_LOAD_SUCCESSFUL);
    setInitialParams(queryParams, navigate, TableName.FIELD_SUPPORT);
    dispatch(fetchDynamicFilters(true));
  }, []);

  useEffect(() => {
    unsubscribe.current = poll(
      () => fetchFieldSupportData(),
      json => json,
      CSA_POLLING_TIME,
      noop
    );

    return () => {
      unsubscribe.current?.();
    };
  }, []);

  const toggleSingleTicketDrawer = () => {
    setShowSingleTicketDrawer(!showSingleTicketDrawer);
  };

  const fetchFieldSupportData = () => {
    dispatch(fetchTicketsForTable(userId, true));
  };

  const viewDetailsHandler = useCallback(
    (ticket: ITableTicket) => {
      const { newUpdatesCount, referenceId } = ticket || {};
      if (newUpdatesCount) {
        acknowledgeTicket(referenceId, () => {
          dispatch(updateAcknowlegementStatus(fieldSupportTickets, ticket, true));
        });
      }
      addClickstreamEvent(CLICKSTREAM_EVENT_NAMES.LH_TASK_VIEW_DETAIL_CLICKED, {
        ticketId: referenceId
      });
      dispatch(fetchSingleTicket(referenceId));
      setShowSingleTicketDrawer(true);
    },
    [data]
  );

  const colDefs = useMemo(() => {
    const colData = getFieldSupportColumnDefs(viewDetailsHandler);
    const newColumnData = getUpdatedColDefs(colData, queryParams, TableName.FIELD_SUPPORT);
    return newColumnData;
  }, [queryParams?.fieldSupport?.sortByField, viewDetailsHandler]);

  const handleCellClick = (cell: any) => {
    if (cell.colDef.field === 'status') {
      return;
    }
    viewDetailsHandler(cell.data);
  };

  return (
    <div>
      <TableHeader
        filterSchema={fieldSupportFilterSchema}
        fetchData={fetchFieldSupportData}
        tableName={TableName.FIELD_SUPPORT}
        clickstreamTableName={FIELD_SUPPORT_TABLE}
      />
      <RenderAgTable
        key={queryParams?.fieldSupport?.sortByField}
        data={data}
        colDefs={colDefs}
        tableName={TableName.FIELD_SUPPORT}
        tableHeight={window?.innerHeight - 280} // 280 is header height
        isServerSortable={true}
        callBackFn={fetchFieldSupportData}
        pageDetails={{ totalElements, currentPage, pageSize }}
        headerHeight={60}
        customTableClass="csaTable"
        onCellClicked={handleCellClick}
        rowHeight={64}
        rowClass={'cursor-pointer'}
      />
      <SingleTicketDrawer
        isOpen={showSingleTicketDrawer}
        toggleDrawer={toggleSingleTicketDrawer}
        ticket={singleTicket}
        isLoading={singleTicketLoading}
      />
      <Loader show={tableDataIsLoading} className={styles.loadingState} animate={false} />
    </div>
  );
};

export default FieldSupport;
